import {
    Button,
    CheckboxFormField,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
    FormFieldInput,
    Icon,
    Label,
} from "@/shared/components";
import { timeRegex } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { Vestiging } from "@/entities/vestiging";
import { VestigingCard } from "./vestiging-card";

const hoursMinutesString = z.string().trim().regex(timeRegex, { message: "Voer een geldige tijd in (HH:MM)" });

const extensionApprovalSchema = z.object({
    accepted: z.boolean(),
    nieuwOpenTot: hoursMinutesString,
});

const extensionRequestSchema = z.object({
    nieuwOpenTot: hoursMinutesString,
});

interface VerlengenOpeningstijdenDialogProps {
    vestiging: Vestiging;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    onCancel: () => void;
    onUpdateOpeningstijd: (vestiging: Vestiging, openTot: string) => Promise<void>;
}

export function VerlengenOpeningstijdenDialog({
    vestiging,
    open,
    onOpenChange,
    onCancel,
    onUpdateOpeningstijd,
}: Readonly<VerlengenOpeningstijdenDialogProps>) {
    const form = useForm<z.infer<typeof extensionApprovalSchema>>({
        resolver: zodResolver(extensionApprovalSchema),
        defaultValues: { accepted: false, nieuwOpenTot: "" },
    });

    const onSubmit = async (data: z.infer<typeof extensionApprovalSchema>) => {
        await onUpdateOpeningstijd(vestiging, data.nieuwOpenTot);
        onOpenChange(false);
    };

    const isAccepted = form.watch("accepted");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <Form {...form}>
                <DialogContainer className="max-w-md">
                    <form
                        onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && isAccepted) {
                                e.preventDefault();
                                void form.handleSubmit(onSubmit)();
                            }
                        }}
                    >
                        <DialogHeader>
                            <DialogTitle>Openingstijd verlengen</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-4">
                            <DialogDescription>
                                Neem contact op met de 2e Wachtdient NS Stations om verlengde openingstijden aan te vragen.
                            </DialogDescription>
                            <div data-testid="wachtdienst-tel">
                                <h3 className="text-s mb-1 font-bold">Telefoonnummer 2e Wachtdienst NS Stations</h3>
                                <a href={`tel:088-671 87 69`} className="hover:underline">
                                    088-671 87 69
                                </a>
                            </div>
                            <div className="flex flex-col justify-start gap-4">
                                <h3 className="sr-only">Vestiging</h3>
                                <VestigingCard vestiging={vestiging} showContact={false} />
                                <CheckboxFormField form={form} name="accepted" label="Verlengde openingstijden zijn goedgekeurd" />
                                {isAccepted && (
                                    <div className="flex items-start gap-2" data-testid="sluitingstijd-comparison">
                                        <div className="w-[8ch]">
                                            <div className="grid gap-1">
                                                <Label htmlFor="value-huidig" className="text-content-emphasis leading-l text-s font-bold">
                                                    Huidig
                                                </Label>
                                                <input
                                                    id="value-huidig"
                                                    className="bg-elevated-sunken text-m flex w-full min-w-0 rounded-md border px-2.75 py-1.75 shadow-xs"
                                                    value={vestiging.currentOpeningstijden.openTot}
                                                    readOnly
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        <Icon name="arrow-right" className="text-content-emphasis relative top-3 self-center" />
                                        <FormFieldInput form={form} name="nieuwOpenTot" label="Nieuwe sluitingstijd" className="w-[15ch]" />
                                    </div>
                                )}
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            <Button variant="tertiary" onClick={onCancel}>
                                Annuleren
                            </Button>
                            <Button variant="primary" type="submit" disabled={!isAccepted}>
                                Opslaan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContainer>
            </Form>
        </Dialog>
    );
}

interface VerzoekVerlengenOpeningstijdenDialogProps {
    vestiging: Vestiging;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    onCancel: () => void;
    onUpdateOpeningstijd: (vestiging: Vestiging, openTot: string) => Promise<void>;
}

export function VerzoekVerlengenOpeningstijdenDialog({
    vestiging,
    open,
    onOpenChange,
    onCancel,
    onUpdateOpeningstijd,
}: Readonly<VerzoekVerlengenOpeningstijdenDialogProps>) {
    const form = useForm<z.infer<typeof extensionRequestSchema>>({
        resolver: zodResolver(extensionRequestSchema),
        defaultValues: { nieuwOpenTot: "" },
    });

    const onSubmit = async (data: z.infer<typeof extensionRequestSchema>) => {
        await onUpdateOpeningstijd(vestiging, data.nieuwOpenTot);
        onOpenChange(false);
    };

    const isActief = vestiging.aangevraagdeVestiging?.status === "Actief";
    const submitButtonText = isActief ? "Verzoek versturen" : "Opslaan";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <Form {...form}>
                <DialogContainer className="max-w-md">
                    <form
                        onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                void form.handleSubmit(onSubmit)();
                            }
                        }}
                    >
                        <DialogHeader>
                            <DialogTitle>Verzoek openingstijd verlengen</DialogTitle>
                        </DialogHeader>
                        <DialogBody className="grid gap-4">
                            <DialogDescription>Kies tot hoe laat deze vestiging open moet blijven.</DialogDescription>
                            <div className="flex flex-col justify-start gap-4">
                                <h3 className="sr-only">Vestiging</h3>
                                <VestigingCard vestiging={vestiging} showContact={false} />
                                <div className="flex items-start gap-2">
                                    <div className="w-[8ch]">
                                        <div className="grid gap-1">
                                            <Label
                                                htmlFor="value-huidig-lokaal"
                                                className="text-content-emphasis leading-l text-s font-bold"
                                            >
                                                Huidig
                                            </Label>
                                            <input
                                                id="value-huidig-lokaal"
                                                className="bg-elevated-sunken text-m flex w-full min-w-0 rounded-md border px-2.75 py-1.75 shadow-xs"
                                                value={vestiging.currentOpeningstijden.openTot}
                                                readOnly
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <Icon name="arrow-right" className="text-content-emphasis relative top-3 self-center" />
                                    <FormFieldInput form={form} name="nieuwOpenTot" label="Nieuwe sluitingstijd" className="w-[15ch]" />
                                </div>
                                <div className="text-content-subtle">
                                    <p className="text-s" data-testid="openingstijden-helptext">
                                        Dit verzoek voor langere openingstijden wordt doorgestuurd naar de Klantenservice. Zij nemen contact
                                        op met de vestiging.
                                    </p>
                                </div>
                            </div>
                        </DialogBody>

                        <DialogFooter>
                            <Button variant="tertiary" onClick={onCancel}>
                                Annuleren
                            </Button>
                            <Button variant="primary" type="submit">
                                {submitButtonText}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContainer>
            </Form>
        </Dialog>
    );
}
